import { Menu, ActionIcon, Tooltip, Modal, Text, Button, Group, Stack } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import {
  IconPower,
  IconRefresh,
  IconPlayerStop,
  IconBolt,
  IconChevronDown,
} from '@tabler/icons-react';
import { useState } from 'react';
import { useWakeOnLan, useCheckDeviceStatus } from '@/hooks';
import type { OnlineStatus, DeviceStatus } from '@/hooks';

interface PowerControlMenuProps {
  deviceId: string;
  deviceName: string;
  status: OnlineStatus;
  deviceStatus?: DeviceStatus;
  onStatusUpdated?: () => void;
}

type ActionType = 'wol' | 'reboot' | 'shutdown' | null;

export function PowerControlMenu({
  deviceId,
  deviceName,
  status,
  deviceStatus,
  onStatusUpdated,
}: PowerControlMenuProps) {
  const [confirmAction, setConfirmAction] = useState<ActionType>(null);
  const [confirmOpened, { open: openConfirm, close: closeConfirm }] = useDisclosure(false);

  const wakeOnLan = useWakeOnLan();
  const checkStatus = useCheckDeviceStatus();

  const isOnline = status === 'online';
  const hasWinRM = deviceStatus?.winrmAvailable ?? false;

  const handleAction = (action: ActionType) => {
    if (action === 'wol') {
      // WoL doesn't need confirmation
      wakeOnLan.mutate(
        { deviceId, initiatedBy: 'ui' },
        {
          onSuccess: () => {
            onStatusUpdated?.();
          },
        }
      );
    } else {
      // Reboot/shutdown need confirmation
      setConfirmAction(action);
      openConfirm();
    }
  };

  const handleConfirm = () => {
    if (confirmAction === 'reboot' || confirmAction === 'shutdown') {
      // TODO: Implement reboot/shutdown when WinRM service is added
      console.log(`${confirmAction} requested for device ${deviceId}`);
    }
    closeConfirm();
    setConfirmAction(null);
  };

  const handleRefresh = () => {
    checkStatus.mutate(deviceId, {
      onSuccess: () => {
        onStatusUpdated?.();
      },
    });
  };

  const getConfirmationMessage = () => {
    switch (confirmAction) {
      case 'reboot':
        return `Are you sure you want to reboot ${deviceName}?`;
      case 'shutdown':
        return `Are you sure you want to shut down ${deviceName}?`;
      default:
        return '';
    }
  };

  return (
    <>
      <Group gap={4}>
        <Tooltip label="Refresh status">
          <ActionIcon
            variant="subtle"
            size="sm"
            onClick={handleRefresh}
            loading={checkStatus.isPending}
          >
            <IconRefresh size={16} />
          </ActionIcon>
        </Tooltip>

        <Menu shadow="md" width={180} position="bottom-end">
          <Menu.Target>
            <Tooltip label="Power actions">
              <ActionIcon variant="subtle" size="sm">
                <IconPower size={16} />
                <IconChevronDown size={10} style={{ marginLeft: 2 }} />
              </ActionIcon>
            </Tooltip>
          </Menu.Target>

          <Menu.Dropdown>
            <Menu.Label>Power Actions</Menu.Label>

            <Menu.Item
              leftSection={<IconBolt size={14} />}
              onClick={() => handleAction('wol')}
              disabled={isOnline || wakeOnLan.isPending}
              color={isOnline ? 'gray' : undefined}
            >
              Wake on LAN
            </Menu.Item>

            <Menu.Divider />

            <Menu.Item
              leftSection={<IconRefresh size={14} />}
              onClick={() => handleAction('reboot')}
              disabled={!isOnline || !hasWinRM}
              color={!isOnline || !hasWinRM ? 'gray' : 'yellow'}
            >
              Reboot
              {!hasWinRM && isOnline && (
                <Text size="xs" c="dimmed" ml={4}>
                  (No WinRM)
                </Text>
              )}
            </Menu.Item>

            <Menu.Item
              leftSection={<IconPlayerStop size={14} />}
              onClick={() => handleAction('shutdown')}
              disabled={!isOnline || !hasWinRM}
              color={!isOnline || !hasWinRM ? 'gray' : 'red'}
            >
              Shutdown
              {!hasWinRM && isOnline && (
                <Text size="xs" c="dimmed" ml={4}>
                  (No WinRM)
                </Text>
              )}
            </Menu.Item>
          </Menu.Dropdown>
        </Menu>
      </Group>

      <Modal
        opened={confirmOpened}
        onClose={closeConfirm}
        title={
          <Group gap="xs">
            {confirmAction === 'reboot' ? (
              <IconRefresh size={20} color="var(--mantine-color-yellow-6)" />
            ) : (
              <IconPlayerStop size={20} color="var(--mantine-color-red-6)" />
            )}
            <Text fw={600}>
              Confirm {confirmAction === 'reboot' ? 'Reboot' : 'Shutdown'}
            </Text>
          </Group>
        }
        centered
        size="sm"
      >
        <Stack gap="md">
          <Text>{getConfirmationMessage()}</Text>

          <Text size="sm" c="dimmed">
            This action will be executed via PowerShell Remoting (WinRM).
          </Text>

          <Group justify="flex-end" gap="sm">
            <Button variant="default" onClick={closeConfirm}>
              Cancel
            </Button>
            <Button
              color={confirmAction === 'shutdown' ? 'red' : 'yellow'}
              onClick={handleConfirm}
            >
              {confirmAction === 'reboot' ? 'Reboot' : 'Shut Down'}
            </Button>
          </Group>
        </Stack>
      </Modal>
    </>
  );
}
